import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button, Typography } from '@mui/material';
import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import {
  ConfirmIcon,
  OverwriteIcon,
  ResetIcon,
  TaskIcon,
  ViewDetailIcon,
} from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import { getTaskStatusLabelVariant } from 'app/pages/companyManager/CompanyManagerPage/tabs/Tasks/TasksTab.utils';
import { navigateToFieldRef } from 'app/services/loanNavigation';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import {
  LabelSize,
  LabelVariant,
} from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import { ModalSize } from 'app/shared/componentsRestyled/coreDesignSystem/Modals';
import PendingChangeWrapperCDS from 'app/shared/componentsRestyled/coreDesignSystem/PendingChangeWrapperCDS';
import StatusIconCDS from 'app/shared/componentsRestyled/coreDesignSystem/StatusIconCDS';
import dayjs from 'dayjs';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { kebabCase } from 'lodash';
import { nanoid } from 'nanoid';
import PropTypes from 'prop-types';

import { ChecklistItemDeterminationType, ChecklistItemResultValueType } from 'constants/checklists';
import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { FeatureFlag } from 'constants/featureFlags';
import { LoanApplicationTabRouteParam, TasksSubtabRouteParam } from 'constants/loanApplication';
import { PAGE_CODES } from 'constants/pageCodes';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { TaskStatus } from 'constants/tasks';
import usePopover from 'hooks/usePopover';
import {
  formatChecklistValue,
  getChecklistResultStatus,
  isChecklistResultStateNonPassing,
  isChecklistStateRowDetermination,
} from 'utils/checklist';
import { getConditionalArray } from 'utils/data';
import { getTaskStatusLabel } from 'utils/tasks';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { openDialogAction, showMessageAction } from 'store/global/actions';
import { changeApplicationChecklistResultAction } from 'store/loanApplications/actions';
import { getApplicationChecklistByIdAction } from 'store/loanApplications/actions/checklists.actions';
import { addTaskAction } from 'store/loanApplications/actions/tasks.actions';
import { selectChecklistBorrowerTargetId } from 'store/loanApplications/selectors/checklists.selectors';
import { selectPrimaryBorrowerIdCommonInfo } from 'store/loanApplications/selectors/loanApplicationCommonInfo.selectors';
import { selectTasks } from 'store/loanApplications/selectors/tasks.selectors';
import { selectUserById } from 'store/users/selectors';

import { AlertsTestIds } from '../Alerts.testIds';
import { getStatusIconPendingChangeWrapperProps } from '../Alerts.utils';
import useAlertsActionContext from '../hooks/useAlertsActionContext';
import OverridePlaceholder from '../OverridePlaceholder';

import {
  checklistInfoStyles,
  clickableRowStyles,
  getChecklistActionContainerStyles,
  getChecklistActionInfoContainerStyles,
  getChecklistStatusContainerStyles,
  statusIconContainerStyles,
  statusTextContainerStyles,
  valueContainerStyles,
} from './ChecklistItem.styles';
import {
  getActionBody,
  getActionParams,
  normalizeCalculationsPendingChange,
} from './ChecklistItem.utils';

const dialogId = nanoid();

const ChecklistItemInfo = ({
  item,
  nestedLevel,
  isEmpty = false,
  setIsLoading,
  disabled,
  statusDisplayType,
}) => {
  const dispatch = useDispatch();

  const { applicationId } = useParams();
  const history = useHistory();
  // "Go to field" resolves the Underwriting tab route against this toggle (LEN2-499).
  const isRestyledUnderwritingTabEnabled = useFeatureToggle(
    FeatureFlag.EnableRestyledUnderwritingTab,
  );

  const { isManageAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_CHECKLISTS));
  const primaryBorrowerId = useSelector(selectPrimaryBorrowerIdCommonInfo);
  const checklistBorrowerTargetId = useSelector(selectChecklistBorrowerTargetId(item.checklistId));
  const { isViewAllowed: isTasksViewAllowed, isManageAllowed: isTasksManageAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.TASKS),
  );

  const { setIsActionInProgress } = useAlertsActionContext();
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const tasksMap = useSelector(selectTasks);

  const [isValueTruncated, setIsValueTruncated] = useState(false);

  const { buttonText, buttonVariant, isRemoveOverride, calculation } = useMemo(() => {
    const normalizedItem = normalizeCalculationsPendingChange({ item });

    return {
      ...getActionParams({ item: normalizedItem }),
      ...normalizedItem,
    };
  }, [item]);

  const {
    overridenBy,
    overridenByName,
    overrideDate,
    pendingResult,
    expressionResult: determination,
    result,
    wasOverriden,
    pendingChangeStatus,
    state,
  } = calculation;

  const isStateRow = isChecklistStateRowDetermination(item.expressionResult);
  const isNotApplicable = item.expressionResult === ChecklistItemDeterminationType.NOT_APPLICABLE;
  const isReviewable =
    calculation.type === ChecklistItemResultValueType.MANUAL ||
    item.expressionResult === ChecklistItemDeterminationType.NEEDS_REVIEW;
  const isValue = calculation.type === ChecklistItemResultValueType.VALUE && !isStateRow;
  const isValueDisplayed = isValue && !isChecklistResultStateNonPassing(state);
  const isFieldLinked = !!item.fieldRef;
  const {
    iconType: statusIconType,
    text: statusText,
    textColor: statusTextColor,
  } = getChecklistResultStatus({
    state,
    determination: item.expressionResult,
    result: pendingResult || result,
    statusDisplayType,
  });

  const overridenByUser = useSelector(selectUserById(overridenBy));
  // FH-3004: the flat read resolves this name BE-side onto the item; the legacy (flag-OFF) read
  // carries no such field, so this falls back to the FE user-id lookup above.
  const resolvedOverridenByName = overridenByName ?? overridenByUser?.name;
  const hasOverrideInfo = !isValue && (!!overrideDate || !!resolvedOverridenByName);

  const isOverridePendingChanged = !!pendingChangeStatus?.change;
  const isOverrideAllowed = !result || wasOverriden || isOverridePendingChanged;

  const onActionClick = useCallback(
    e => {
      e.stopPropagation();

      setIsLoading(true);
      setIsActionInProgress(true);

      dispatch(
        changeApplicationChecklistResultAction({
          ...getActionBody({ isRemoveOverride, itemId: item.id }),
          checklistId: item.checklistId,
          applicationId,
        }),
      )
        .then(() => {
          return dispatch(
            getApplicationChecklistByIdAction({
              applicationId,
              checklistId: item.checklistId,
              includeResults: true,
              includeItems: true,
              includePendingChanges: true,
            }),
          );
        })
        .then(() => {
          const message = 'Action was successfully applied';
          dispatch(showMessageAction({ message, variant: 'success' }));
        })
        .catch(Error)
        .finally(() => {
          setIsLoading(false);
          setIsActionInProgress(false);
        });
    },
    [
      applicationId,
      dispatch,
      isRemoveOverride,
      item.checklistId,
      item.id,
      setIsActionInProgress,
      setIsLoading,
    ],
  );

  const formattedDetermination = useMemo(
    () => formatChecklistValue({ value: determination, valueFormat: item.valueFormat }),
    [determination, item.valueFormat],
  );

  const stateReason = isStateRow || isValue ? formattedDetermination : null;

  const formattedDeterminationLines = useMemo(
    () =>
      formattedDetermination?.split('\\n').map(subStr => (
        <React.Fragment key={subStr}>
          {subStr}
          <br />
        </React.Fragment>
      )),
    [formattedDetermination],
  );

  const onShowClick = useCallback(
    e => {
      e.stopPropagation();

      dispatch(
        openDialogAction({
          id: dialogId,
          isRestyled: true,
          size: ModalSize.SMALL,
          body: () => (
            <Typography className="w-full" color="text.secondary" component="p" variant="regular14">
              {formattedDeterminationLines}
            </Typography>
          ),
        }),
      );
    },
    [dispatch, formattedDeterminationLines],
  );

  const onValueContainerRef = useCallback(node => {
    if (!node) {
      return;
    }

    setIsValueTruncated(node.scrollWidth > node.clientWidth);
  }, []);

  const statusIconPendingChangeWrapperProps = useMemo(
    () => getStatusIconPendingChangeWrapperProps(item.isPendingChange),
    [item.isPendingChange],
  );

  const existingTask = useMemo(() => {
    if (!item.taskTypeId) {
      return null;
    }

    return (
      Object.values(tasksMap || {}).find(
        task =>
          task.taskTypeId === item.taskTypeId &&
          task.status !== TaskStatus.CANCELED &&
          task.status !== TaskStatus.REJECTED,
      ) || null
    );
  }, [item.taskTypeId, tasksMap]);

  const isCreateTaskVisible =
    result === false &&
    !!item.taskTypeId &&
    !isValue &&
    !isNotApplicable &&
    isManageAllowed &&
    isTasksViewAllowed &&
    isTasksManageAllowed &&
    !disabled;

  const isCreateTaskPrimary =
    !isFieldLinked && !isReviewable && isCreateTaskVisible && !existingTask;

  const viewTaskUrl = useMemo(
    () =>
      LoansPaths.applicationManagement.generatePath({
        [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
        [LOANS_ROUTING_PARAMS.tab]: LoanApplicationTabRouteParam.TASKS,
        [LOANS_ROUTING_PARAMS.subtab]: TasksSubtabRouteParam.PENDING,
      }),
    [applicationId],
  );

  const onCreateTaskClick = useCallback(
    e => {
      e.stopPropagation();

      setIsLoading(true);
      setIsActionInProgress(true);

      dispatch(
        addTaskAction({
          applicationId,
          taskTypeId: item.taskTypeId,
          name: item.createTaskButtonLabel || item.name,
          description: item.name,
          availableForAssignees: true,
        }),
      )
        .then(() => {
          dispatch(showMessageAction({ message: 'Task created successfully', variant: 'success' }));
        })
        .finally(() => {
          setIsLoading(false);
          setIsActionInProgress(false);
        });
    },
    [
      applicationId,
      dispatch,
      item.createTaskButtonLabel,
      item.name,
      item.taskTypeId,
      setIsActionInProgress,
      setIsLoading,
    ],
  );

  const onGoToFieldClick = useCallback(
    e => {
      e.stopPropagation();

      navigateToFieldRef({
        parameters: {
          fieldRef: item.fieldRef,
          borrowerId: checklistBorrowerTargetId ?? primaryBorrowerId,
        },
        context: { history, applicationId, isRestyledUnderwritingTabEnabled },
      });
    },
    [
      history,
      applicationId,
      isRestyledUnderwritingTabEnabled,
      item.fieldRef,
      checklistBorrowerTargetId,
      primaryBorrowerId,
    ],
  );

  const overflowItems = useMemo(
    () => [
      // Override/Remove Override for auto (TRUE/FALSE) items → always overflow
      ...getConditionalArray(!isReviewable && !isValue && !!buttonText && isOverrideAllowed, () => [
        {
          text: buttonText,
          'data-testid': AlertsTestIds.getItemActionId(item.id, kebabCase(buttonText)),
          onClick: onActionClick,
          isDisabled: !isManageAllowed || disabled,
          icon: isRemoveOverride ? (
            <ResetIcon size={IconSize[20]} />
          ) : (
            <OverwriteIcon size={IconSize[20]} />
          ),
        },
      ]),
      // Complete / Mark Reviewed (or Reset) → overflow when displaced by Go to field
      ...getConditionalArray(
        isFieldLinked && isReviewable && !!buttonText && !isValue && isOverrideAllowed,
        () => [
          {
            text: buttonText,
            'data-testid': AlertsTestIds.getItemActionId(item.id, kebabCase(buttonText)),
            onClick: onActionClick,
            isDisabled: !isManageAllowed || disabled,
            icon: isRemoveOverride ? (
              <ResetIcon size={IconSize[20]} />
            ) : (
              <ConfirmIcon size={IconSize[20]} />
            ),
          },
        ],
      ),
      // View Task → always overflow
      ...getConditionalArray(isCreateTaskVisible && !!existingTask, () => [
        {
          text: 'View Task',
          'data-testid': AlertsTestIds.getItemActionId(item.id, 'view-task'),
          href: viewTaskUrl,
          icon: <ViewDetailIcon size={IconSize[20]} />,
        },
      ]),
      // Create Task → overflow when not the designated primary
      ...getConditionalArray(isCreateTaskVisible && !existingTask && !isCreateTaskPrimary, () => [
        {
          text: '+ Create Task',
          'data-testid': AlertsTestIds.getItemActionId(item.id, 'create-task'),
          onClick: onCreateTaskClick,
          isDisabled: disabled,
          icon: <TaskIcon size={IconSize[20]} />,
        },
      ]),
    ],
    [
      item.id,
      isReviewable,
      isValue,
      buttonText,
      isOverrideAllowed,
      isRemoveOverride,
      isFieldLinked,
      onActionClick,
      isManageAllowed,
      disabled,
      isCreateTaskVisible,
      existingTask,
      viewTaskUrl,
      onCreateTaskClick,
      isCreateTaskPrimary,
    ],
  );

  const onRowClick = isFieldLinked && isEmpty ? onGoToFieldClick : undefined;

  const rowSx = useMemo(
    () =>
      isFieldLinked && isEmpty ? [checklistInfoStyles, clickableRowStyles] : checklistInfoStyles,
    [isFieldLinked, isEmpty],
  );

  return (
    <Box sx={rowSx} onClick={onRowClick}>
      <Box sx={getChecklistStatusContainerStyles({ nestedLevel, isEmpty })}>
        {!isValueDisplayed && (
          <>
            <Box sx={statusIconContainerStyles}>
              <PendingChangeWrapperCDS {...statusIconPendingChangeWrapperProps}>
                <StatusIconCDS isOutlined size={IconSize[24]} type={statusIconType} />
              </PendingChangeWrapperCDS>
            </Box>
            {(statusText || stateReason) && (
              <Box sx={statusTextContainerStyles}>
                {statusText && (
                  <Typography
                    color={statusTextColor}
                    data-testid={AlertsTestIds.getItemStatusLabelId(item.id)}
                    variant="medium14"
                  >
                    {statusText}
                  </Typography>
                )}
                {stateReason && (
                  <Typography
                    noWrap
                    color="text.secondary"
                    data-testid={AlertsTestIds.getItemStateReasonId(item.id)}
                    title={stateReason}
                    variant="regular12"
                  >
                    {stateReason}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
        {isValueDisplayed && (
          <Box ref={onValueContainerRef} sx={valueContainerStyles}>
            {!isValueTruncated && (
              <Typography className="checklistItemValue" variant="regular14">
                {formattedDetermination}
              </Typography>
            )}

            {isValueTruncated && (
              <Button
                data-testid={AlertsTestIds.VIEW_RESULT_BUTTON}
                disabled={disabled}
                size="small"
                type="button"
                onClick={onShowClick}
              >
                View Result
              </Button>
            )}
          </Box>
        )}
      </Box>
      <Box sx={getChecklistActionInfoContainerStyles({ nestedLevel, isEmpty })}>
        {hasOverrideInfo ? (
          <PendingChangeWrapperCDS
            isSmall
            className="block !w-full"
            pendingChangesActionType={
              item.isPendingChange ? PendingChangesActionType.MODIFIED : undefined
            }
          >
            <LabelCDS
              size={LabelSize.S}
              title={
                resolvedOverridenByName
                  ? `Overridden · ${resolvedOverridenByName}`
                  : dayjs(overrideDate).format(COMMON_DATETIME_FORMAT)
              }
              variant={LabelVariant.ORANGE}
            />
          </PendingChangeWrapperCDS>
        ) : (
          <OverridePlaceholder />
        )}
      </Box>

      <Box sx={getChecklistActionContainerStyles({ nestedLevel, isEmpty })}>
        {isCreateTaskVisible && !!existingTask && (
          <LabelCDS
            size={LabelSize.S}
            title={getTaskStatusLabel(existingTask.status)}
            variant={getTaskStatusLabelVariant(existingTask.status)}
          />
        )}

        {isFieldLinked && (
          <Button
            data-testid={AlertsTestIds.GO_TO_FIELD_BUTTON}
            size="small"
            type="button"
            onClick={onGoToFieldClick}
          >
            Go to field
          </Button>
        )}

        {!isFieldLinked && isReviewable && !!buttonText && !isValue && isOverrideAllowed && (
          <Button
            data-testid={AlertsTestIds.ACTION_BUTTON}
            disabled={!isManageAllowed || disabled}
            size="small"
            type="submit"
            variant={buttonVariant}
            onClick={onActionClick}
          >
            {buttonText}
          </Button>
        )}

        {isCreateTaskPrimary && (
          <Button
            data-testid={AlertsTestIds.CREATE_TASK_BUTTON}
            size="small"
            variant="outlined"
            onClick={onCreateTaskClick}
          >
            + Create Task
          </Button>
        )}

        {overflowItems.length > 0 && (
          <>
            <MoreButtonCDS
              data-testid={AlertsTestIds.getItemActionsButtonId(item.id)}
              isSelected={!!anchorEl}
              onClick={onSetAnchorElement}
            />
            <MenuPopoverCDS
              anchorEl={anchorEl}
              items={overflowItems}
              onClose={onClearAnchorElement}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

ChecklistItemInfo.propTypes = {
  item: PropTypes.shape({
    categoryId: PropTypes.string.isRequired,
    checklistId: PropTypes.string.isRequired,
    expressionResult: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    orderSequence: PropTypes.number.isRequired,
    expression: PropTypes.object,
    nestedFields: PropTypes.array,
    calculation: PropTypes.object,
    parentId: PropTypes.string,
    visibilityExpression: PropTypes.object,
    taskTypeId: PropTypes.string,
    createTaskButtonLabel: PropTypes.string,
    fieldRef: PropTypes.string,
  }).isRequired,
  nestedLevel: PropTypes.number.isRequired,
  statusDisplayType: PropTypes.string.isRequired,
  isEmpty: PropTypes.bool,
  ...WithLoadingPropTypes,
};

export default withLoading(ChecklistItemInfo);
